<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2" v-if="isFetched && mapPanelDataFetched">
      <card-title-row translation-path="sidebar.graphGeneration" :has-buttons="false">
        <template #subtitle>
          <italic-subheader translation-path="sidebar.saveGraphToFile" class="text-left" />
        </template>
      </card-title-row>
      <card-title-row is-subtitle translation-path="sidebar.defineExportParameters" class="pt-5" />
      <sidebar-row class="pt-2">
        <sidebar-select-row
          font-size="14px"
          :value="statusModel"
          :select-props="statusProps"
          @input="statusModel = $event"
          translation-path="sidebar.status"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20, 'nudge-top': -5 }"
          required
          align="center"
        />
      </sidebar-row>
      <sidebar-row>
        <sidebar-select-row
          class="my-5"
          font-size="14px"
          :value="netKindModel"
          :select-props="netKindProps"
          @input="netKindModel = $event"
          translation-path="sidebar.networkType"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20 }"
          required
          align="center"
        />
      </sidebar-row>
      <sidebar-row border-bottom>
        <graph-generation-params-list :parameters.sync="additionalGraphParameters" />
      </sidebar-row>
      <sidebar-row class="pt-4">
        <v-checkbox v-model="useBillingData">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.useBillingData') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row>
        <sidebar-date-picker-row
          translation-path="sidebar.computeWaterUsage"
          :value="waterUsageDate"
          @input="waterUsageDate = $event"
          :disabled="!useBillingData"
        />
      </sidebar-row>
      <sidebar-row :border-bottom="isScadaJunctionsEnabled" class="pt-4">
        <v-row no-gutters class="justify-space-between">
          <v-col cols="4" class="billingDataText" :class="useBillingData ? '' : 'text--disabled'">
            {{ $i18n.t('sidebar.selectBillingDemandMethod') }}:
          </v-col>
          <v-col cols="7">
            <v-radio-group v-model="billing_demand_method" :disabled="!useBillingData">
              <v-radio value="closest">
                <template #label>
                  <div :class="useBillingData ? 'billingDataText' : 'billingDataTextDisabled'">
                    {{ $i18n.t('sidebar.billingDemandMethodClosest') }}
                  </div>
                </template>
              </v-radio>
              <v-radio value="divide">
                <template #label>
                  <div :class="useBillingData ? 'billingDataText' : 'billingDataTextDisabled'">
                    {{ $i18n.t('sidebar.billingDemandMethodDivide') }}
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row v-if="isScadaJunctionsEnabled" class="pt-4">
        <v-checkbox v-model="useScadaData">
          <template #label>
            <div class="billingDataText">
              {{ $i18n.t('sidebar.useScadaData') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
      <sidebar-row v-if="isScadaJunctionsEnabled">
        <v-row no-gutters class="justify-space-between">
          <v-col cols="4" class="billingDataText" :class="useScadaData ? '' : 'text--disabled'">
            {{ $i18n.t('sidebar.scadaSince') }}:
          </v-col>
          <v-col cols="7">
            <data-input
              :data-type="dataTypeDate"
              v-model="scada_since"
              :disabled="!useScadaData"
              prepend-icon="mdi-calendar"
            />
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';
import MainLoader from '@/components/MainLoader';
import SidebarSelectRow from '@/components/SidebarSelectRow';
import SidebarDatePickerRow from '@/components/SidebarDatePickerRow';
import DataInput from '@/components/DataInput';
import GraphGenerationParamsList from '@/components/GraphGenerationParamsList';

export default {
  name: 'GraphGeneration',
  components: {
    DataInput,
    SidebarDatePickerRow,
    SidebarSelectRow,
    MainLoader,
    SidebarRow,
    ItalicSubheader,
    CardTitleRow,
    GraphGenerationParamsList,
  },
  data: vm => ({
    scada_since: localStorage.getItem('scada_since')
      ? JSON.parse(localStorage.getItem('scada_since'))
      : vm
          .$dayjs(new Date())
          .subtract(1, 'day')
          .toISOString()
          .slice(0, 10),
    statusModel: localStorage.getItem('statusModel') ? JSON.parse(localStorage.getItem('statusModel')) : [],
    netKindModel: localStorage.getItem('netKindModel') ? JSON.parse(localStorage.getItem('netKindModel')) : [],
    useBillingData: localStorage.getItem('useBillingData') ? JSON.parse(localStorage.getItem('useBillingData')) : false,
    useScadaData: localStorage.getItem('useScadaData') ? JSON.parse(localStorage.getItem('useScadaData')) : false,
    billing_demand_method: localStorage.getItem('billing_demand_method')
      ? JSON.parse(localStorage.getItem('billing_demand_method'))
      : 'closest',
    additionalGraphParameters: localStorage.getItem('additionalGraphParameters')
      ? JSON.parse(localStorage.getItem('additionalGraphParameters'))
      : [],
    dataTypeDate: { name: 'date' },
    isButtonLoading: false,
    isExportLoading: false,
    isModelLoading: false,
    isFetched: false,
    waterUsageDate: [],
  }),
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    netTypes: get('epanet/netKind'),
    statusItems: get('epanet/status'),
    isEpanetRunAnalysisEnabled: get('admin/settingsValues@epanet_run_analysis_enabled'),
    isScadaJunctionsEnabled: get('admin/settingsValues@scada_junctions_module_enabled'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    stickyButtons: sync('sidebar/stickyButtons'),
    computedNetTypesItems() {
      const old = this.netTypes.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    computedStatusItems() {
      const old = this.statusItems.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    statusProps() {
      return {
        items: this.computedStatusItems,
        multiple: true,
        chips: true,
        autoConvertSelect: false,
        hideDetails: true,
      };
    },
    netKindProps() {
      return {
        items: this.computedNetTypesItems,
        multiple: true,
        chips: true,
        hideDetails: true,
      };
    },
    computedWaterUsage() {
      return this.waterUsageDate.map(value => {
        if (this.waterUsageDate.indexOf(value) === 1) {
          const lastDay = this.$dayjs(this.waterUsageDate[1]).daysInMonth();
          return value + `-${lastDay}`;
        } else {
          return value + '-01';
        }
      });
    },
    isValid() {
      return (
        (this.useBillingData ? this.computedWaterUsage.length === 2 : true) &&
        (this.useScadaData ? this.scada_since : true) &&
        this.netKindModel.length > 0 &&
        this.statusModel.length > 0
      );
    },
    computedStickybuttons() {
      return [
        {
          action: 'generateGraph',
          translationPath: 'sidebar.export',
          loading: this.isExportLoading,
          disabled: this.isModelLoading,
        },
        ...(this.isEpanetRunAnalysisEnabled
          ? [
              {
                action: 'epanetAnalysisModel',
                translationPath: 'sidebar.model',
                cancelLike: true,
                loading: this.isModelLoading,
                disabled: this.isExportLoading,
              },
            ]
          : []),
      ];
    },
  },
  methods: {
    exportFile: call('epanet/getEpanet'),
    runModel: call('epanet/runModel'),
    getStatus: call('epanet/getStatus'),
    getNetKind: call('epanet/getNetKind'),

    async init() {
      await Promise.all([this.getStatus({}), this.getNetKind({})]);
      this.isStickyButtonsVisible = true;
      this.isStickyButtonsValid = this.isValid;
      this.stickyButtons = this.computedStickybuttons;
      this.$root.$off('generateGraph');
      this.$root.$on('generateGraph', this.generateGraph);
      this.$root.$off('epanetAnalysisModel');
      this.$root.$on('epanetAnalysisModel', this.epanetAnalysisModel);
      this.isFetched = true;
    },
    setLocalStorageItems() {
      localStorage.setItem('statusModel', JSON.stringify(this.statusModel));
      localStorage.setItem('netKindModel', JSON.stringify(this.netKindModel));
      localStorage.setItem('useBillingData', JSON.stringify(this.useBillingData));
      localStorage.setItem('useScadaData', JSON.stringify(this.useScadaData));
      localStorage.setItem('scada_since', JSON.stringify(this.scada_since));
      localStorage.setItem('billing_demand_method', JSON.stringify(this.billing_demand_method));
      localStorage.setItem('additionalGraphParameters', JSON.stringify(this.additionalGraphParameters));
    },
    getPayload() {
      let additionalParamsArray = [];
      this.additionalGraphParameters.forEach(param => {
        if (param.checked) {
          additionalParamsArray.push({
            filter: param.filter,
            datasource: param.datasource,
          });
        }
      });
      const payload = {
        status: this.statusModel,
        net_kind: this.netKindModel,
        billing: this.useBillingData,
        scada: this.useScadaData,
        ...(this.useScadaData && { scada_since: this.scada_since }),
        ...(this.useBillingData &&
          this.computedWaterUsage.length === 2 && { billing_since: this.computedWaterUsage[0] }),
        ...(this.useBillingData &&
          this.computedWaterUsage.length === 2 && { billing_until: this.computedWaterUsage[1] }),
        ...(this.useBillingData && { billing_demand_method: this.billing_demand_method }),
        features_filter: additionalParamsArray,
      };
      return payload;
    },
    async epanetAnalysisModel() {
      this.isModelLoading = true;
      try {
        const r = await this.runModel(this.getPayload());
        this.$router.push({
          name: 'modelingResults',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isModelLoading = false;
      }
    },
    async generateGraph() {
      this.isExportLoading = true;
      this.setLocalStorageItems();
      try {
        return await this.exportFile(this.getPayload());
      } finally {
        this.isExportLoading = false;
      }
    },
  },
  watch: {
    isValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    computedStickybuttons(nV) {
      this.stickyButtons = nV;
    },
    isScadaJunctionsEnabled: {
      immediate: true,
      handler(nV) {
        if (!nV) this.useScadaData = nV;
      },
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
  },
};
</script>

<style scoped lang="scss">
.billingDataText {
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  color: #202124;
}
.billingDataTextDisabled {
  text-align: left;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.38);
}
::v-deep {
  .v-input--selection-controls {
    margin-top: 0;
  }
}
</style>
